/**
 * Particle + shockwave FX for Waves. Obstacle passes spawn expanding
 * rings and spark bursts; death and milestones get heavier bursts.
 * Hold-boost embers trail behind the player while the button is held.
 */

import type { RenderQuality } from '../../rendering/renderQuality';
import type { WavesShockwaveSpec } from './wavesRenderTypes';
import { SHOCKWAVE_POOL_MAX } from './wavesRenderConstants';

export interface WavesParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  colour: string;
}

export interface WavesParticleBurstSpec {
  x: number;
  y: number;
  colour: string;
  count: number;
  life: number;
  size: number;
  angleStart: number;
  angleEnd: number;
  speedMin: number;
  speedMax: number;
}

export interface WavesShockwave {
  x: number;
  y: number;
  startR: number;
  endR: number;
  life: number;
  maxLife: number;
  thickness: number;
  colour: string;
  alpha: number;
}

const MAX_PARTICLES = 256;

/**
 * Peak opacity a particle reaches at birth (it fades to 0 over its
 * life). Held below 1 so the additive-blended bursts read as
 * translucent sparks — incoming hazards and the corridor lane the
 * player is diving through stay visible THROUGH the FX even when passes
 * stack up at high scroll speed. (The ship draws after this pass, so it
 * always reads crisp on top of its own wake.)
 */
const PARTICLE_PEAK_ALPHA = 0.72;

export class WavesFxSystem {
  private readonly particles: WavesParticle[] = [];
  private readonly shockwaves: WavesShockwave[] = [];
  private readonly consumedShockwaves = new WeakSet<WavesShockwaveSpec>();
  private reducedMotion: boolean;
  private particleHardCap: number;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.particleHardCap = Math.round(MAX_PARTICLES * quality.particleScale);
  }

  private scaleCount(baseCount: number): number {
    if (this.particleHardCap >= MAX_PARTICLES) {
      return baseCount;
    }
    return Math.max(1, Math.round(baseCount * (this.particleHardCap / MAX_PARTICLES)));
  }

  emit(spec: WavesParticleBurstSpec): void {
    if (this.reducedMotion) {
      return;
    }
    const count = this.scaleCount(spec.count);
    for (let i = 0; i < count; i += 1) {
      if (this.particles.length >= this.particleHardCap) {
        break;
      }
      const angle = spec.angleStart + (i / count) * (spec.angleEnd - spec.angleStart);
      const speed = spec.speedMin + Math.random() * (spec.speedMax - spec.speedMin);
      this.particles.push({
        x: spec.x,
        y: spec.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: spec.life,
        maxLife: spec.life,
        size: spec.size,
        colour: spec.colour,
      });
    }
  }

  consumeShockwaveSpecs(specs: ReadonlyArray<WavesShockwaveSpec>): void {
    if (specs.length === 0 || this.reducedMotion) {
      return;
    }
    for (const spec of specs) {
      if (this.consumedShockwaves.has(spec)) {
        continue;
      }
      this.consumedShockwaves.add(spec);
      this.shockwaves.push({
        x: spec.x,
        y: spec.y,
        startR: spec.startR,
        endR: spec.endR,
        life: spec.life,
        maxLife: spec.life,
        thickness: spec.thickness,
        colour: spec.colour,
        alpha: spec.alpha,
      });
      if (this.shockwaves.length > SHOCKWAVE_POOL_MAX) {
        this.shockwaves.splice(0, this.shockwaves.length - SHOCKWAVE_POOL_MAX);
      }
    }
  }

  step(dt: number): void {
    if (this.particles.length > 0) {
      let write = 0;
      for (let read = 0; read < this.particles.length; read += 1) {
        const p = this.particles[read];
        p.life -= dt;
        if (p.life <= 0) {
          continue;
        }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.95;
        p.vy *= 0.95;
        if (write !== read) {
          this.particles[write] = p;
        }
        write += 1;
      }
      this.particles.length = write;
    }

    if (this.shockwaves.length > 0) {
      let write = 0;
      for (let read = 0; read < this.shockwaves.length; read += 1) {
        const w = this.shockwaves[read];
        w.life -= dt;
        if (w.life <= 0) {
          continue;
        }
        if (write !== read) {
          this.shockwaves[write] = w;
        }
        write += 1;
      }
      this.shockwaves.length = write;
    }
  }

  draw(ctx: CanvasRenderingContext2D, chromeShadows: boolean): void {
    this.drawShockwaves(ctx, chromeShadows);
    this.drawParticles(ctx);
  }

  private drawShockwaves(ctx: CanvasRenderingContext2D, chromeShadows: boolean): void {
    if (this.shockwaves.length === 0) {
      return;
    }
    ctx.save();
    for (const w of this.shockwaves) {
      const t = w.life / w.maxLife;
      const eased = 1 - t;
      const radius = w.startR + (w.endR - w.startR) * eased;
      ctx.globalAlpha = w.alpha * t * t;
      ctx.strokeStyle = w.colour;
      ctx.lineWidth = w.thickness * (0.6 + 0.4 * t);
      if (chromeShadows) {
        ctx.shadowColor = w.colour;
        ctx.shadowBlur = 12 * t;
      }
      ctx.beginPath();
      ctx.arc(w.x, w.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    if (this.particles.length === 0) {
      return;
    }
    ctx.save();
    // Additive blend so bursts brighten what's behind them instead of
    // painting over it — incoming hazards read THROUGH the particles,
    // keeping the juice without masking the sightline. The ship draws
    // after this pass, so it stays crisp on top regardless.
    ctx.globalCompositeOperation = 'lighter';
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life / p.maxLife) * PARTICLE_PEAK_ALPHA;
      ctx.fillStyle = p.colour;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  reset(): void {
    this.particles.length = 0;
    this.shockwaves.length = 0;
  }
}
